import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fileSrc } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import TopBar from "../components/TopBar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { ChevronLeft, Bell, FileCheck2, StickyNote } from "lucide-react";
import OverviewTab from "../components/tabs/OverviewTab";
import CommunicationTab from "../components/tabs/CommunicationTab";
import BudgetTab from "../components/tabs/BudgetTab";
import ApprovalsTab from "../components/tabs/ApprovalsTab";
import MoodBoardTab from "../components/tabs/MoodBoardTab";
import AIAssistantTab from "../components/tabs/AIAssistantTab";
import DocumentsTab from "../components/tabs/DocumentsTab";
import CalendarTab from "../components/tabs/CalendarTab";
import FFETab from "../components/tabs/FFETab";
import ChecklistsTab from "../components/tabs/ChecklistsTab";
import PurchaseOrdersTab from "../components/tabs/PurchaseOrdersTab";
import OrdersTab from "../components/tabs/OrdersTab";
import InvoicesTab from "../components/tabs/InvoicesTab";
import ProposalsTab from "../components/tabs/ProposalsTab";
import PhotosTab from "../components/tabs/PhotosTab";
import TimeTrackingTab from "../components/tabs/TimeTrackingTab";
import ProductLibraryTab from "../components/tabs/ProductLibraryTab";
import PhaseStepper, { PHASE_COLORS } from "../components/PhaseStepper";
import ExportPdfButton from "../components/ExportPdfButton";
import ClientAccessDialog from "../components/ClientAccessDialog";
import { Button } from "../components/ui/button";
import { ShieldCheck, Compass } from "lucide-react";
import ProjectTour from "../components/ProjectTour";
import Footer from "../components/Footer";

const STATUS_COLORS = PHASE_COLORS;

const SECTION_MAP = {
  overview: "overview", budget: "budget", approvals: "approvals", ffe: "ffe",
  documents: "documents", moodboards: "moodboards", communication: "notes", calendar: "calendar",
  invoices: "invoices", checklists: "checklists", time: "time", po: "po",
};

const TABS = [
  { v: "overview", label: "Overview" },
  { v: "budget", label: "Budget" },
  { v: "approvals", label: "Approvals" },
  { v: "ffe", label: "FF&E" },
  { v: "orders", label: "Orders", designerOnly: true },
  { v: "po", label: "Purchase Orders", designerOnly: true },
  { v: "invoices", label: "Invoices", designerOnly: true },
  { v: "proposals", label: "Proposals" },
  { v: "checklists", label: "Checklists / Assignments" },
  { v: "time", label: "Time Tracking", designerOnly: true },
  { v: "documents", label: "Documents" },
  { v: "moodboards", label: "Mood Boards" },
  { v: "scheme", label: "Design Scheme" },
  { v: "communication", label: "Notes" },
  { v: "calendar", label: "Calendar" },
  { v: "photos", label: "Photos" },
  { v: "library", label: "Product Library", designerOnly: true },
  { v: "assistant", label: "AI Assistant" },
];

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, previewClient } = useAuth();
  const [project, setProject] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [notif, setNotif] = useState({ unread_notes: 0, new_approvals: 0 });
  const [accessOpen, setAccessOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);

  const loadProject = () => {
    api.get(`/projects/${id}`).then((r) => setProject(r.data)).catch(() => setNotFound(true));
  };
  useEffect(loadProject, [id]);

  // Entering/exiting client preview resets to a client-viewable tab
  useEffect(() => { if (previewClient) setActiveTab("overview"); }, [previewClient]);

  const loadNotif = () => api.get(`/projects/${id}/notifications`).then((r) => setNotif(r.data)).catch(() => {});
  useEffect(() => {
    loadNotif();
    const t = setInterval(loadNotif, 8000);
    return () => clearInterval(t);
  }, [id]);

  // Mark approvals seen when viewing the Approvals tab; refresh notif when switching tabs
  useEffect(() => {
    if (activeTab === "approvals") {
      api.post(`/projects/${id}/approvals/mark-seen`).catch(() => {});
    }
    const t = setTimeout(loadNotif, 3000);
    return () => clearTimeout(t);
  }, [activeTab, id]);

  if (notFound)
    return (
      <div className="min-h-screen bg-[#F9F8F6]">
        <TopBar />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <h1 className="font-serif text-3xl text-[#2B2622]">Project not found</h1>
          <button onClick={() => navigate("/")} className="mt-4 text-[#A68A6B] underline">Back to projects</button>
        </div>
      </div>
    );

  if (!project)
    return (
      <div className="min-h-screen bg-[#F9F8F6]">
        <TopBar />
        <div className="max-w-7xl mx-auto px-6 py-24 text-[#5C5651]">Loading…</div>
      </div>
    );

  const c = STATUS_COLORS[project.status] || "#A68A6B";
  const isClient = user?.role === "client" || previewClient;
  const viewingAsDesigner = user?.role === "designer" && !previewClient;
  const readOnly = isClient;
  const perms = project.client_permissions || {};
  const canView = (m) => !isClient || (perms[m]?.view ?? true);
  const canEdit = (m) => !isClient || !!perms[m]?.edit;
  const tourSteps = TABS
    .filter((t) => (!t.designerOnly || viewingAsDesigner) && canView(t.v))
    .map((t) => ({ tab: t.v, label: t.label, highlight: ["ffe", "moodboards", "scheme", "library"].includes(t.v) }));

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="project-detail-page">
      <TopBar />
      <main className="max-w-7xl mx-auto px-6 sm:px-8 py-10">
        <button data-testid="back-btn" onClick={() => navigate("/")}
          className="inline-flex items-center gap-1 text-sm text-[#5C5651] hover:text-[#A68A6B] mb-6">
          <ChevronLeft size={16} /> All projects
        </button>
        {viewingAsDesigner && (
          <div className="flex justify-end -mt-2 mb-2 gap-2">
            <Button data-testid="start-tour-btn" onClick={() => setTourOpen(true)}
              variant="outline"
              className="rounded-none border-[#EBE5E0] text-[#5C5651] hover:border-[#A68A6B] hover:text-[#A68A6B] uppercase tracking-wider text-xs">
              <Compass size={14} strokeWidth={1.5} className="mr-2 text-[#A68A6B]" /> Take the Tour
            </Button>
            <Button data-testid="open-client-access-btn" onClick={() => setAccessOpen(true)}
              variant="outline"
              className="rounded-none border-[#EBE5E0] text-[#5C5651] hover:border-[#A68A6B] hover:text-[#A68A6B] uppercase tracking-wider text-xs">
              <ShieldCheck size={14} strokeWidth={1.5} className="mr-2 text-[#A68A6B]" /> Client Access
            </Button>
          </div>
        )}
        <ClientAccessDialog projectId={id} open={accessOpen} onOpenChange={setAccessOpen} onSaved={loadProject} />
        {viewingAsDesigner && (
          <ProjectTour open={tourOpen} steps={tourSteps} onGoTab={setActiveTab} onClose={() => setTourOpen(false)} />
        )}

        {(notif.unread_notes > 0 || notif.new_approvals > 0) && (
          <div data-testid="notification-bar"
            className="flex flex-wrap items-center gap-3 border border-[#A68A6B] bg-[#F2EFEB] px-4 py-3 mb-6">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-[#8C7154]">
              <Bell size={16} strokeWidth={1.5} />
              {notif.unread_notes + notif.new_approvals} new {notif.unread_notes + notif.new_approvals === 1 ? "update" : "updates"}
            </span>
            {notif.new_approvals > 0 && (
              <button data-testid="notif-approvals" onClick={() => setActiveTab("approvals")}
                className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider bg-white border border-[#EBE5E0] px-3 py-1.5 hover:border-[#A68A6B] text-[#2B2622]">
                <FileCheck2 size={14} strokeWidth={1.5} className="text-[#506B52]" />
                {notif.new_approvals} new {notif.new_approvals === 1 ? "approval" : "approvals"}
              </button>
            )}
            {notif.unread_notes > 0 && (
              <button data-testid="notif-notes" onClick={() => setActiveTab("communication")}
                className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider bg-white border border-[#EBE5E0] px-3 py-1.5 hover:border-[#A68A6B] text-[#2B2622]">
                <StickyNote size={14} strokeWidth={1.5} className="text-[#A68A6B]" />
                {notif.unread_notes} new {notif.unread_notes === 1 ? "note" : "notes"}
              </button>
            )}
          </div>
        )}

        <div className="border-b border-[#EBE5E0] pb-8 mb-8">
          <div className="flex items-start justify-between gap-6">
            <div className="min-w-0">
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-wider mb-3" style={{ color: c }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: c }} /> {project.status}
              </span>
              <h1 data-testid="project-title" className="font-serif text-4xl sm:text-5xl text-[#2B2622]">{project.name}</h1>
              <p className="text-[#5C5651] mt-3 max-w-2xl">{project.description}</p>
            </div>
            {(project.designer_logo || project.designer_business_name) && (
              <div data-testid="project-branding" className="flex items-center gap-3 shrink-0 border border-[#EBE5E0] bg-white px-4 py-3">
                {project.designer_logo && (
                  <img data-testid="project-branding-logo" src={fileSrc(project.designer_logo)} alt={project.designer_business_name || "Studio logo"}
                    className="h-10 w-auto max-w-[120px] object-contain" />
                )}
                {project.designer_business_name && (
                  <div className="text-right leading-tight">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-[#A68A6B]">Designed by</p>
                    <p data-testid="project-branding-name" className="font-serif text-lg text-[#2B2622]">{project.designer_business_name}</p>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="mt-6 max-w-xl">
            <PhaseStepper status={project.status} showLabels />
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-transparent border-b border-[#EBE5E0] rounded-none w-full justify-start h-auto p-0 gap-1 overflow-x-auto flex-nowrap">
            {TABS.filter((t) => (!t.designerOnly || viewingAsDesigner)
              && canView(t.v)).map((t) => {
              const badge = t.v === "communication" ? notif.unread_notes : t.v === "approvals" ? notif.new_approvals : 0;
              return (
                <TabsTrigger key={t.v} value={t.v} data-testid={`tab-${t.v}`}
                  className="relative rounded-none data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-[#A68A6B] data-[state=active]:text-[#2B2622] text-[#5C5651] uppercase tracking-wider text-xs px-4 py-3 whitespace-nowrap">
                  {t.label}
                  {badge > 0 && (
                    <span data-testid={`tab-badge-${t.v}`}
                      className="ml-1.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 text-[10px] bg-[#A68A6B] text-white rounded-full">
                      {badge}
                    </span>
                  )}
                </TabsTrigger>
              );
            })}
          </TabsList>

          <div className="py-8">
            {viewingAsDesigner && SECTION_MAP[activeTab] && (
              <div className="flex justify-end mb-4" data-testid="pdf-export-bar">
                <ExportPdfButton projectId={id} section={SECTION_MAP[activeTab]} />
              </div>
            )}
            <TabsContent value="overview"><OverviewTab project={project} readOnly={readOnly} onUpdate={loadProject} /></TabsContent>
            <TabsContent value="communication"><CommunicationTab projectId={id} readOnly={!canEdit("communication")} /></TabsContent>
            <TabsContent value="calendar"><CalendarTab projectId={id} readOnly={readOnly} /></TabsContent>
            <TabsContent value="photos"><PhotosTab projectId={id} readOnly={!canEdit("photos")} /></TabsContent>
            <TabsContent value="budget"><BudgetTab projectId={id} readOnly={readOnly} budgetTotal={project.budget_total} project={project} onProjectUpdate={loadProject} clientHideTradeMarkup={isClient && !!perms.budget?.hide_trade_markup} /></TabsContent>
            <TabsContent value="approvals"><ApprovalsTab projectId={id} readOnly={readOnly} canRespond={canEdit("approvals")} /></TabsContent>
            <TabsContent value="ffe"><FFETab projectId={id} readOnly={readOnly} /></TabsContent>
            <TabsContent value="checklists"><ChecklistsTab projectId={id} readOnly={readOnly} /></TabsContent>
            {viewingAsDesigner && (
              <TabsContent value="time"><TimeTrackingTab projectId={id} /></TabsContent>
            )}
            {viewingAsDesigner && (
              <TabsContent value="po"><PurchaseOrdersTab projectId={id} project={project} /></TabsContent>
            )}
            {viewingAsDesigner && (
              <TabsContent value="orders"><OrdersTab projectId={id} /></TabsContent>
            )}
            {viewingAsDesigner && (
              <TabsContent value="invoices"><InvoicesTab projectId={id} project={project} /></TabsContent>
            )}
            <TabsContent value="proposals"><ProposalsTab projectId={id} project={project} /></TabsContent>
            {viewingAsDesigner && (
              <TabsContent value="library"><ProductLibraryTab /></TabsContent>
            )}
            <TabsContent value="documents"><DocumentsTab projectId={id} readOnly={readOnly} /></TabsContent>
            <TabsContent value="moodboards"><MoodBoardTab projectId={id} readOnly={!canEdit("moodboards")} /></TabsContent>
            <TabsContent value="scheme"><MoodBoardTab projectId={id} readOnly={!canEdit("scheme")} kind="scheme" /></TabsContent>
            <TabsContent value="assistant"><AIAssistantTab projectId={id} readOnly={!canEdit("assistant")} /></TabsContent>
          </div>
        </Tabs>
      </main>
    </div>
  );
}
